import {IClientOptionsSettings} from "./client-options-settings.model";

export interface ChatbotRegressionTestCase {
    name: string;
    expectedClassificationPromptContains?: string | null;
    forbiddenClassificationPromptContains?: string | null;
    expectedAssistantInstructionsContains?: string | null;
    forbiddenAssistantInstructionsContains?: string | null;
    expectedAssistantRef?: string | null;
    expectedBaseUrlContains?: string | null;
    expectedDefaultLocale?: string | null;
    expectedAiProductCardsEnabled?: boolean | null;
    enabled?: boolean;
}

export interface IChatbotOptions extends IClientOptionsSettings {
    baseUrl: string;
    conversationRetentionDays?: number | null;
    gptClassificationAndSanitizeQuery: string;
    classificationModel?: string;
    gptLuceneQuery: string;
    assistantInstructions: string;
    aiProductCardsEnabled?: boolean;
    aiProductCardsInstruction?: string;
    conversationCategories?: string[];
    // Dynamic widget popup generation
    dynamicWidgetText: boolean;
    dynamicWidgetTextInstruction: string;
    usePromptCaching?: boolean;
    frontEndOptions: IFrontEndOptions;
    widgetOptions: IWidgetOptions;
    assistantRefs: string[];
    regressionTestCases?: ChatbotRegressionTestCase[];
    chatSummaryPrompt: string;
    proposeSampleMessagesPrompt: string;
    cookiesToCollect: string[];
    hubSpotSync?: IHubSpotSyncOptions;
    localization?: IChatbotLocalizationOptions;
}

export interface IChatbotLocalizationOptions {
    defaultLocale: string;
    supportedLocales: string[];
    autoGenerate: boolean;
    variants: Record<string, IChatbotLocalizationVariant>;
}

export interface IChatbotLocalizationVariant {
    frontEndOptions?: Partial<IFrontEndOptions>;
    widgetOptions?: Partial<IWidgetOptions>;
    generatedFromHash?: string;
    generatedAt?: string;
}

export interface IHubSpotSyncOptions {
    enabled: boolean;
    inboxId: string;
    channelId?: string;
    channelAccountId?: string;
    connectionId?: string;
    portalId?: string;
    handoverPolicy?: string;
    pauseBotOnHandover?: boolean;
    lastSyncStatus?: string;
    lastSyncError?: string;
    lastSyncAt?: string;
}

export const DEFAULT_WIDGET_Z_INDEX = 3001;
export const MIN_WIDGET_Z_INDEX = 0;
export const MAX_WIDGET_Z_INDEX = 2147483647;

export function normalizeWidgetZIndex(value: unknown): number {
    const parsed = typeof value === 'number' ? value : Number.NaN;
    return Number.isInteger(parsed) && parsed >= MIN_WIDGET_Z_INDEX && parsed <= MAX_WIDGET_Z_INDEX
        ? parsed
        : DEFAULT_WIDGET_Z_INDEX;
}

export function normalizeConversationCategories(value: unknown): string[] {
    const rawText = Array.isArray(value)
        ? value.map(entry => `${entry ?? ''}`).join('\n')
        : `${value ?? ''}`;
    const entries = rawText
        .replace(/\r/g, '')
        .split(/[\n,]/)
        .map(entry => entry.trim().replace(/\s+/g, ' '))
        .filter(entry => !!entry && entry.toLowerCase() !== 'uncategorized');

    const seen = new Set<string>();
    const categories: string[] = [];
    entries.forEach(entry => {
        const key = entry.toLowerCase();
        if (seen.has(key)) {
            return;
        }

        seen.add(key);
        categories.push(entry.length <= 64 ? entry : entry.slice(0, 64));
    });

    return categories;
}
export interface IFrontEndOptions {
    productCardTextOverrides?: import('./product-card-texts.model').ProductCardTextOverrides;
    name: string;
    logo: string;
    headerDisplayMode?: string;
    logoWidth: string;
    chatAvatar: string;
    chatAvatarEnabled?: boolean;
    layoutPreset?: string;
    zIndex?: number;
    introMessage: string;
    inputPlaceholder: string;
    submitButtonIcon?: string;
    resetButtonIcon?: string;
    attachButtonIcon?: string;
    voiceButtonIcon?: string;
    compactSubmitButtonIcon?: string;
    compactResetButtonIcon?: string;
    resetButtonTooltip?: string;
    resetButtonConfirmationEnabled?: boolean;
    resetButtonConfirmationText?: string;
    sampleMessagesTitle?: string;
    sampleMessagesPresentation?: string;
    sampleMessagesHoverBorderColor?: string;
    sampleMessagesClickAnimationEnabled?: boolean;
    sampleMessagesClickAnimationDelayMs?: number;
    dropdownBtnText: string;
    dropdownBtnTextClose: string;
    dropdownMessage: string;
    cardType: string;

    // Added missing properties
    cardLinkUrl: string;
    cardUtmString: string;
    cardLocale: string;
    cardCurrency: string;
    productCardDetailsLabel?: string;
    productCardTestDriveLabel?: string;
    productCardPresentation: string;
    productCardPresentationMode?: 'fixed' | 'dynamic';
    productCardDynamicPresentations?: string[];
    addToCartEnabled?: boolean;
    productCardEcommerceTrackingEnabled?: boolean;
    ga4EcommerceEventsEnabled?: boolean;

    voiceEnabled: boolean;
    attachmentsEnabled: boolean;
    imageComposition?: IImageCompositionOptions;
    dropdownEnabled: boolean;
    dropdownBtnIconEnabled: boolean;
    firstMessage: string;
    css: string;
    js: string;
    errorMessages: ITemplateMessage[];
    waitMessages: ITemplateMessage[];
    sampleMessages: ISampleMessage[];
    styling: IStyling;
}

export interface IImageCompositionOptions {
    enabled: boolean;
    cameraCaptureEnabled: boolean;
    eligibleCategories: string[];
    disclaimerText: string;
    // Active request transport. Widget requests carry image bytes in MessageRequest.attachments[].base64.
    preferredRequestTransport: 'attachments-base64' | 'upload-reference' | string;

    // upload-reference is reserved/future artifact-store metadata, not an active storage backend.
    supportedRequestTransports: Array<'attachments-base64' | 'upload-reference' | string>;

    // Reserved for future upload-reference artifact expiry.
    uploadExpiryHours: number;

    // Reserved for future generated-result artifact expiry.
    resultExpiryHours: number;

    // Reserved for future upload-reference ownership checks.
    ownership: IImageCompositionOwnershipOptions;
}

export interface IImageCompositionOwnershipOptions {
    // Future artifact-store binding scopes for upload-reference inputs.
    uploadBoundTo: string[];

    // Future artifact-store binding scopes for generated results.
    resultBoundTo: string[];
}

export interface IImageCompositionFormValue {
    enabled: boolean;
    cameraCaptureEnabled: boolean;
    eligibleCategoriesText: string;
    disclaimerText: string;
    preferredRequestTransport: string;
    supportedRequestTransports: string[];
    uploadExpiryHours: number;
    resultExpiryHours: number;
    ownership: IImageCompositionOwnershipOptions;
}

export interface IImageCompositionValidationResult {
    normalized: IImageCompositionOptions;
    requestedEnabled: boolean;
    persistedEnabled: boolean;
    categoryError: string | null;
    disclaimerError: string | null;
    activationErrors: string[];
    errors: string[];
    hadDuplicateCategories: boolean;
}

export interface IImageCompositionNormalizationOptions {
    preserveExistingEnabled?: boolean;
}

interface IImageCompositionCategoryParseResult {
    categories: string[];
    hasEmptyValues: boolean;
    invalidCatchAllValues: string[];
    hadDuplicateCategories: boolean;
}

export function createDefaultImageCompositionOptions(): IImageCompositionOptions {
    return {
        enabled: false,
        cameraCaptureEnabled: false,
        eligibleCategories: [],
        disclaimerText: '',
        preferredRequestTransport: 'attachments-base64',
        supportedRequestTransports: ['attachments-base64', 'upload-reference'],
        uploadExpiryHours: 24,
        resultExpiryHours: 24,
        ownership: {
            uploadBoundTo: ['client', 'chatbot', 'conversation'],
            resultBoundTo: ['client', 'chatbot', 'conversation']
        }
    };
}

export function createImageCompositionFormValue(
    options?: Partial<IImageCompositionOptions> | null,
    runtimeReady: boolean = false
): IImageCompositionFormValue {
    const normalized = mergeImageCompositionOptions(options);
    return {
        enabled: runtimeReady ? normalized.enabled === true : false,
        cameraCaptureEnabled: normalized.cameraCaptureEnabled === true,
        eligibleCategoriesText: normalized.eligibleCategories.join('\n'),
        disclaimerText: normalized.disclaimerText,
        preferredRequestTransport: normalized.preferredRequestTransport,
        supportedRequestTransports: [...normalized.supportedRequestTransports],
        uploadExpiryHours: normalized.uploadExpiryHours,
        resultExpiryHours: normalized.resultExpiryHours,
        ownership: {
            uploadBoundTo: [...normalized.ownership.uploadBoundTo],
            resultBoundTo: [...normalized.ownership.resultBoundTo]
        }
    };
}

export function validateAndNormalizeImageCompositionOptions(
    formValue: Partial<IImageCompositionFormValue> | null | undefined,
    existingOptions: Partial<IImageCompositionOptions> | null | undefined,
    attachmentsEnabled: boolean,
    runtimeReady: boolean,
    normalizationOptions: IImageCompositionNormalizationOptions = {}
): IImageCompositionValidationResult {
    const base = mergeImageCompositionOptions(existingOptions);
    const requestedEnabled = formValue?.enabled === true;
    const categoryResult = parseImageCompositionCategories(formValue?.eligibleCategoriesText);
    const disclaimerText = `${formValue?.disclaimerText ?? base.disclaimerText ?? ''}`.trim();

    const activationErrors: string[] = [];
    if (requestedEnabled && !attachmentsEnabled) {
        activationErrors.push('Enable file uploads before activating image composition.');
    }
    if (requestedEnabled && !runtimeReady) {
        activationErrors.push('Runtime image generation is not available until backend/runtime slices pass.');
    }

    let categoryError: string | null = null;
    if (categoryResult.hasEmptyValues) {
        categoryError = 'Remove empty category values.';
    } else if (categoryResult.invalidCatchAllValues.length > 0) {
        categoryError = 'Remove catch-all values such as "all" or "*".';
    } else if (requestedEnabled && categoryResult.categories.length === 0) {
        categoryError = 'Add at least one eligible category before activation.';
    }

    const disclaimerError = requestedEnabled && disclaimerText.length === 0
        ? 'Add consent/disclaimer text before activation.'
        : null;

    const errors = [
        ...activationErrors,
        ...(categoryError ? [categoryError] : []),
        ...(disclaimerError ? [disclaimerError] : [])
    ];
    const persistedEnabled = normalizationOptions.preserveExistingEnabled === true && base.enabled === true && !requestedEnabled
        ? true
        : requestedEnabled && errors.length === 0;

    return {
        normalized: {
            enabled: persistedEnabled,
            cameraCaptureEnabled: formValue?.cameraCaptureEnabled === true,
            eligibleCategories: categoryResult.categories,
            disclaimerText,
            preferredRequestTransport: `${formValue?.preferredRequestTransport ?? base.preferredRequestTransport}`.trim() || base.preferredRequestTransport,
            supportedRequestTransports: Array.isArray(formValue?.supportedRequestTransports) && formValue!.supportedRequestTransports.length > 0
                ? [...formValue!.supportedRequestTransports]
                : [...base.supportedRequestTransports],
            uploadExpiryHours: normalizePositiveInteger(formValue?.uploadExpiryHours, base.uploadExpiryHours),
            resultExpiryHours: normalizePositiveInteger(formValue?.resultExpiryHours, base.resultExpiryHours),
            ownership: {
                uploadBoundTo: Array.isArray(formValue?.ownership?.uploadBoundTo) && formValue!.ownership!.uploadBoundTo.length > 0
                    ? [...formValue!.ownership!.uploadBoundTo]
                    : [...base.ownership.uploadBoundTo],
                resultBoundTo: Array.isArray(formValue?.ownership?.resultBoundTo) && formValue!.ownership!.resultBoundTo.length > 0
                    ? [...formValue!.ownership!.resultBoundTo]
                    : [...base.ownership.resultBoundTo]
            }
        },
        requestedEnabled,
        persistedEnabled,
        categoryError,
        disclaimerError,
        activationErrors,
        errors,
        hadDuplicateCategories: categoryResult.hadDuplicateCategories
    };
}

function mergeImageCompositionOptions(options?: Partial<IImageCompositionOptions> | null): IImageCompositionOptions {
    const defaults = createDefaultImageCompositionOptions();
    return {
        ...defaults,
        ...(options ?? {}),
        eligibleCategories: Array.isArray(options?.eligibleCategories)
            ? [...options.eligibleCategories]
            : [...defaults.eligibleCategories],
        supportedRequestTransports: Array.isArray(options?.supportedRequestTransports)
            ? [...options.supportedRequestTransports]
            : [...defaults.supportedRequestTransports],
        ownership: {
            ...defaults.ownership,
            ...(options?.ownership ?? {}),
            uploadBoundTo: Array.isArray(options?.ownership?.uploadBoundTo)
                ? [...options.ownership.uploadBoundTo]
                : [...defaults.ownership.uploadBoundTo],
            resultBoundTo: Array.isArray(options?.ownership?.resultBoundTo)
                ? [...options.ownership.resultBoundTo]
                : [...defaults.ownership.resultBoundTo]
        }
    };
}

function parseImageCompositionCategories(value: unknown): IImageCompositionCategoryParseResult {
    const rawText = Array.isArray(value)
        ? value.map(entry => `${entry ?? ''}`).join('\n')
        : `${value ?? ''}`;
    const trimmedText = rawText.replace(/\r/g, '').trim();

    if (!trimmedText) {
        return {
            categories: [],
            hasEmptyValues: false,
            invalidCatchAllValues: [],
            hadDuplicateCategories: false
        };
    }

    const entries = trimmedText
        .split(/[\n,]/)
        .map(entry => entry.trim());

    const seen = new Set<string>();
    const categories: string[] = [];
    const invalidCatchAllValues: string[] = [];
    let hasEmptyValues = false;
    let hadDuplicateCategories = false;

    entries.forEach(entry => {
        if (!entry) {
            hasEmptyValues = true;
            return;
        }

        const normalized = entry.toLowerCase();
        if (normalized === 'all' || normalized === '*') {
            invalidCatchAllValues.push(entry);
            return;
        }

        if (seen.has(normalized)) {
            hadDuplicateCategories = true;
            return;
        }

        seen.add(normalized);
        categories.push(entry);
    });

    return {
        categories,
        hasEmptyValues,
        invalidCatchAllValues,
        hadDuplicateCategories
    };
}

function normalizePositiveInteger(value: unknown, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return fallback;
    }
    return Math.floor(parsed);
}


export interface ISampleMessage {
    headline: string;
    description: string;
    imageUrl?: string;
    imageAlt?: string;
    category?: string;
}

export interface ITemplateMessage {
    message: string;
}

export interface IStyling {
    advancedStylingEnabled: boolean;
    chatBotWidth: string;
    font: string;
    fontProvider?: 'google' | 'system' | 'custom' | string;
    fontFamily?: string;
    fontFallback?: string;
    fontPreset?: string;
    fontFaces?: IFontFace[];
    widgetText: string;
    textLinkColor?: string;
    chatBubbleAssistant: string;
    chatBubbleAssistantBorderRadiusDesktop?: string;
    chatBubbleAssistantBorderRadiusMobile?: string;
    chatBubbleAssistantFontSizeDesktop?: string;
    chatBubbleAssistantFontSizeMobile?: string;
    chatBubbleUser: string;
    chatBubbleUserBorderRadius?: string;
    avatarAccent: string;
    chatAvatarSizeDesktop?: string;
    chatAvatarSizeMobile?: string;
    ctaPrimaryBackground: string;
    ctaPrimaryHover: string;
    ctaPrimaryText: string;
    ctaPrimaryTextHover: string;
    badgeBackground: string;
    badgeText: string;
    inputBackground: string;
    inputPlaceholder: string;
    inputBorderColor?: string;
    inputHoverBorderColor?: string;
    inputActiveBorderColor?: string;
    inputActiveSendBackground?: string;
    inputActiveSendColor?: string;
    inputFocus: string;
    inputShadowEnabled: boolean;
    dpBtnBgColor: string;
    dpBtnBgColorHover: string;
    dpBtnColor: string;
    dpBtnColorHover: string;
    recIconColor: string;
    recIconColorHover: string;
    resetButton: string;
    resetButtonHoverBackground?: string;
    resetButtonHoverColor?: string;
    resetButtonPressedBackground?: string;
    resetButtonPressedColor?: string;
    closeButton: string;
    sugBgColor: string;
    sugIconBgColor: string;
    sugIconColor: string;
    cardBtnBgColor: string;
    cardBtnBgColorHover: string;
    cardBtnColor: string;
    cardBtnColorHover: string;
    cardBtnBorderColor?: string;
    cardBtnBorderColorHover?: string;
    cardAddToCartBgColor?: string;
    cardAddToCartBgColorHover?: string;
    cardAddToCartColor?: string;
    cardAddToCartColorHover?: string;
    cardAddToCartBorderColor?: string;
    cardAddToCartBorderColorHover?: string;
    cardWidth?: string;
    cardMaxWidth?: string;
    cardBorderColor?: string;
    cardBackgroundColor?: string;
    cardPadding?: string;
    cardTitleFontSize?: string;
    cardTitleFontWeight?: string;
    cardDescriptionFontSize?: string;
    cardDescriptionVisible?: boolean;
    cardCtaVisible?: boolean;
    cardPriceVisible?: boolean;
    cardPriceFontSize?: string;
    cardPriceFontWeight?: string;
    cardPriceCurrentColor?: string;
    cardPriceSaleCurrentColor?: string;
    cardPriceOriginalColor?: string;
    cardPriceOriginalFontSize?: string;
    cardImageAspectRatio?: string;
    cardCompactImageMaxHeight?: string;
    cardImageBackgroundColor?: string;
    cardBorderRadius?: string;
    cardBadgeBackgroundColor?: string;
    cardBadgeTextColor?: string;
    cardSaleBadgeTextColor?: string;
    cardAttributeBackgroundColor?: string;
    cardAttributeTextColor?: string;
}

export type CustomFontConfigurationField =
    | 'fontFamily'
    | 'fontFaceRegularUrl'
    | 'fontFaceMediumUrl'
    | 'fontFaceSemiBoldUrl'
    | 'fontFaceBoldUrl';

export interface ICustomFontConfigurationValidationResult {
    field: CustomFontConfigurationField | null;
    message: string | null;
}

type CustomFontConfiguration = Partial<IStyling> & {
    fontFaceRegularUrl?: unknown;
    fontFaceMediumUrl?: unknown;
    fontFaceSemiBoldUrl?: unknown;
    fontFaceBoldUrl?: unknown;
};

export function validateCustomFontConfiguration(
    styling: CustomFontConfiguration | null | undefined
): ICustomFontConfigurationValidationResult {
    const provider = `${styling?.fontProvider ?? 'google'}`.trim().toLowerCase();
    if (provider !== 'custom') {
        return {field: null, message: null};
    }

    const preset = `${styling?.fontPreset ?? ''}`.trim();
    if (preset) {
        return {field: null, message: null};
    }

    if (!`${styling?.fontFamily ?? ''}`.trim()) {
        return {
            field: 'fontFamily',
            message: 'Enter a font family for the hosted custom font.'
        };
    }

    if (!`${styling?.fontFaceRegularUrl ?? ''}`.trim()) {
        return {
            field: 'fontFaceRegularUrl',
            message: 'Add a regular font URL or choose a built-in preset.'
        };
    }

    const urlFields: CustomFontConfigurationField[] = [
        'fontFaceRegularUrl',
        'fontFaceMediumUrl',
        'fontFaceSemiBoldUrl',
        'fontFaceBoldUrl'
    ];
    for (const field of urlFields) {
        const value = `${styling?.[field] ?? ''}`.trim();
        if (!value) continue;

        try {
            const url = new URL(value);
            if (url.protocol !== 'https:' && url.protocol !== 'http:') {
                throw new Error('Unsupported font URL protocol');
            }
        } catch {
            return {
                field,
                message: 'Use an absolute HTTP(S) URL for every custom font file.'
            };
        }
    }

    return {field: null, message: null};
}

export interface IFontFace {
    url: string;
    weight?: string | number;
    style?: string;
    format?: string;
}

// models/widget-options.model.ts

export interface IWidgetOptions {
    colors: IColors;
    images: IImages;
    text: IText;
    launcherShape?: 'circle' | 'rectangle' | string;
    launcher?: ILauncherOptions;
    styling?: import('./widget-styling.model').IWidgetStyling;
    popupTailEnabled?: boolean;
    hoverRevealEnabled: boolean;
    positionMode?: 'fixed' | 'sticky';
    autoClose?: IAutoClose;
    mobilePopupOnce?: boolean;
    scrollReveal?: IScrollReveal;
}

export interface ILauncherViewportOptions {
    widthPx?: number;
    heightPx?: number;
    rightPx?: number;
    bottomPx?: number;
}

export interface ILauncherOptions {
    renderMode?: 'framed' | 'image';
    desktop?: ILauncherViewportOptions;
    mobile?: ILauncherViewportOptions;
}

export interface IColors {
    backgroundColor: string;
    textColor: string;
    closeButtonBackground: string;
    closeButtonColor: string;
    closeButtonShadow: string;
    popupShadow: string;
    triangleShadow: string;
    launcherBackground?: string;
    launcherShadow?: string;
}

export interface IImages {
    aiLogo1: string; // Default: "https://respons.markedspartner.no/hubfs/AI-CHATBOT/default-stars.svg"
    aiLogo2: string; // Default: "https://respons.markedspartner.no/hubfs/AI-CHATBOT/default-stars-blink.svg"
    logoSwapEnabled: boolean;
    launcherLogoPadding?: string;
}

export interface IText {
    aiMessage: string; // Default: "How can I help you today?"
}

export interface IAutoClose {
    enabled: boolean;
    delayMs: number;
    devices: IAutoCloseDevices;
    closeOnSameClientReferrer: boolean;
}

export interface IAutoCloseDevices {
    desktop: boolean;
    tablet: boolean;
    mobile: boolean;
}

export interface IScrollReveal {
    enabled: boolean;
    thresholdPx: number;
    devices: IScrollRevealDevices;
}

export interface IScrollRevealDevices {
    desktop: boolean;
    tablet: boolean;
    mobile: boolean;
}
